import { Button } from 'designSystem/components/Button'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'designSystem/components/HoverCard'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/components/DocumentThumbnail'
import { ExternalLink } from 'lucide-react'
import React from 'react'
import styled, { css } from 'styled-components'

const CHIP_LABEL_MAX_CHARS = 12

const PreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 10rem;
  max-width: 16rem;
`

const PreviewTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ThumbnailFrame = styled.div`
  display: flex;
  height: 8rem;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0.25rem;
  background-color: var(--muted);
  padding: 0.375rem;

  > div {
    width: 100%;
    height: 100%;
  }
`

const QuoteText = styled.div`
  font-size: 0.725rem;
  font-style: italic;
  line-height: 1.4;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const QuoteHighlight = styled.span`
  background-color: color-mix(in oklch, var(--chart-3) 35%, transparent);
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  border-radius: 0.125rem;
  padding: 0.0625rem 0.25rem;
`

const ChipButton = styled(Button)<{ $muted?: boolean }>`
  ${({ $muted }) =>
    $muted
      ? css`
          opacity: 0.7;
          color: var(--muted-foreground);
        `
      : null}
`

function truncateChipLabel(label: string): string {
  if (label.length <= CHIP_LABEL_MAX_CHARS) return label
  return `${label.slice(0, CHIP_LABEL_MAX_CHARS).trimEnd()}…`
}

interface EvidenceChipProps {
  documentId?: string
  title?: string
  drawing?: string
  filename?: string
  bqDocumentId?: string
  cantileverDocumentId?: string
  documentHostsHydrated?: boolean
  pageNum: number
  quote: string
  onClick?: () => void
}

export function EvidenceChip({
  documentId,
  title,
  drawing,
  filename,
  bqDocumentId,
  cantileverDocumentId,
  documentHostsHydrated = false,
  pageNum,
  quote,
  onClick,
}: EvidenceChipProps): React.ReactElement {
  const canOpen = Boolean(documentId && onClick)
  const chipLabel = title ?? drawing
  const chipText = chipLabel
    ? `${truncateChipLabel(chipLabel)} · p.${pageNum}`
    : `Page ${pageNum}`
  const previewTitle = chipLabel
    ? `${chipLabel} · p.${pageNum}`
    : `Page ${pageNum}`

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <ChipButton
          type='button'
          variant='outline'
          size='xs'
          $muted={!canOpen}
          aria-disabled={!canOpen}
          onClick={canOpen ? onClick : undefined}>
          {chipText}
          {canOpen ? <ExternalLink data-icon='inline-end' /> : null}
        </ChipButton>
      </HoverCardTrigger>
      <HoverCardContent side='bottom' sideOffset={8}>
        <PreviewBody>
          <PreviewTitle>{previewTitle}</PreviewTitle>
          {documentId ? (
            <ThumbnailFrame>
              <DocumentThumbnail
                documentId={documentId}
                pageNumber={pageNum}
                alt={chipLabel ?? `Page ${pageNum}`}
                filename={filename}
                bqDocumentId={bqDocumentId}
                cantileverDocumentId={cantileverDocumentId}
                documentHostsHydrated={documentHostsHydrated}
              />
            </ThumbnailFrame>
          ) : null}
          <QuoteText>
            <QuoteHighlight>&ldquo;{quote}&rdquo;</QuoteHighlight>
          </QuoteText>
        </PreviewBody>
      </HoverCardContent>
    </HoverCard>
  )
}
